import TaxiCorporationUnbilledOrdersListParams from "../../../../../../common/services/taxi-corporation/unbilled/orders-list/taxi-corporation-unbilled-orders-list-params";
import UnbilledOrder from "../../../../../../common/types/unbilled-order";
import paginationService from "../../../../../../common/utils/pagination/pagination.service";
import BillingsTaxiUnbilledOrderListingFilter, {
  BillingsTaxiUnbilledOrderListingClientFilter,
  BillingsTaxiUnbilledOrderListingDispatchFilter,
  BillingsTaxiUnbilledOrderListingDriverFilter,
  BillingsTaxiUnbilledOrderListingExternalOrderIdFilter,
  BillingsTaxiUnbilledOrderListingInternalOrderIdFilter,
  BillingsTaxiUnbilledOrderListingPassengerFilter,
  BillingsTaxiUnbilledOrderListingRouteAddressFilter,
  BillingsTaxiUnbilledOrderListingRouteDestinationAddressFilter,
  BillingsTaxiUnbilledOrderListingRouteIntermediateAddressFilter,
  BillingsTaxiUnbilledOrderListingRoutePickupAddressFilter,
  BillingsTaxiUnbilledOrderListingStartDateFilter,
  BillingsTaxiUnbilledOrderListingStatusFilter,
} from "../../common/types/billings-taxi-unbilled-order-listing-filter";
import BillingsTaxiUnbilledOrderListingFilterType from "../../common/types/billings-taxi-unbilled-order-listing-filter-type";
import BillingsTaxiUnbilledOrderListingItemStatus from "../../common/types/billings-taxi-unbilled-order-listing-item-status";
import BillingsTaxiUnbilledOrderListingSortKey from "../../common/types/billings-taxi-unbilled-order-listing-sort-key";

const createRequestIsCancelled = (
  filters: BillingsTaxiUnbilledOrderListingFilter[],
): boolean | undefined => {
  const filterValue = filters.find(
    (filter) =>
      filter.type === BillingsTaxiUnbilledOrderListingFilterType.STATUS,
  )?.value as BillingsTaxiUnbilledOrderListingStatusFilter["value"] | undefined;

  if (filterValue === undefined) {
    return undefined;
  }

  if (filterValue === BillingsTaxiUnbilledOrderListingItemStatus.CANCELLED) {
    return true;
  }

  return false;
};

const createRequestOrder = (
  sortKey: BillingsTaxiUnbilledOrderListingSortKey | null,
): UnbilledOrder | undefined => {
  const options: {
    sortKey: BillingsTaxiUnbilledOrderListingSortKey;
    requestOrder: UnbilledOrder;
  }[] = [
    {
      requestOrder: UnbilledOrder.HUMAN_ID_ASC,
      sortKey: BillingsTaxiUnbilledOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: UnbilledOrder.HUMAN_ID_DESC,
      sortKey: BillingsTaxiUnbilledOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: UnbilledOrder.START_TIME_ASC,
      sortKey: BillingsTaxiUnbilledOrderListingSortKey.START_DATE_ASC,
    },
    {
      requestOrder: UnbilledOrder.START_TIME_DESC,
      sortKey: BillingsTaxiUnbilledOrderListingSortKey.START_DATE_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createParams = (
  taxiCorporationUuid: string,
  page: number,
  pageSize: number,
  filters: BillingsTaxiUnbilledOrderListingFilter[],
  sortKey: BillingsTaxiUnbilledOrderListingSortKey | null,
): TaxiCorporationUnbilledOrdersListParams => {
  const offset = paginationService.calculateOffset(page, pageSize);

  return {
    taxiCorporationUuid: taxiCorporationUuid,
    order: createRequestOrder(sortKey),
    address: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledOrderListingFilterType.ROUTE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledOrderListingRouteAddressFilter["value"]
      | undefined,
    limit: pageSize,
    client: filters.find(
      (filter) =>
        filter.type === BillingsTaxiUnbilledOrderListingFilterType.CLIENT,
    )?.value as
      | BillingsTaxiUnbilledOrderListingClientFilter["value"]
      | undefined,
    dispatch: filters.find(
      (filter) =>
        filter.type === BillingsTaxiUnbilledOrderListingFilterType.DISPATCH,
    )?.value as
      | BillingsTaxiUnbilledOrderListingDispatchFilter["value"]
      | undefined,
    driver: filters.find(
      (filter) =>
        filter.type === BillingsTaxiUnbilledOrderListingFilterType.DRIVER,
    )?.value as
      | BillingsTaxiUnbilledOrderListingDriverFilter["value"]
      | undefined,
    endAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledOrderListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledOrderListingRouteDestinationAddressFilter["value"]
      | undefined,
    externalId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledOrderListingFilterType.EXTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiUnbilledOrderListingExternalOrderIdFilter["value"]
      | undefined,
    humanId: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsTaxiUnbilledOrderListingInternalOrderIdFilter["value"]
      | undefined,
    isCancelled: createRequestIsCancelled(filters),
    midAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledOrderListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledOrderListingRouteIntermediateAddressFilter["value"]
      | undefined,
    offset,
    passenger: filters.find(
      (filter) =>
        filter.type === BillingsTaxiUnbilledOrderListingFilterType.PASSENGER,
    )?.value as
      | BillingsTaxiUnbilledOrderListingPassengerFilter["value"]
      | undefined,
    startAddress: filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledOrderListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | BillingsTaxiUnbilledOrderListingRoutePickupAddressFilter["value"]
      | undefined,
    startTimeSince: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiUnbilledOrderListingFilterType.START_DATE,
      )?.value as
        | BillingsTaxiUnbilledOrderListingStartDateFilter["value"]
        | undefined
    )?.from?.toJSON(),
    startTimeTo: (
      filters.find(
        (filter) =>
          filter.type === BillingsTaxiUnbilledOrderListingFilterType.START_DATE,
      )?.value as
        | BillingsTaxiUnbilledOrderListingStartDateFilter["value"]
        | undefined
    )?.to?.toJSON(),
  };
};

const billingsTaxiUnbilledOrderListingRequestFactory = {
  createParams,
};

export default billingsTaxiUnbilledOrderListingRequestFactory;
